/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase from './WireframeBase';
import tools from '../tools';
import wireframeSlider from '../assets/icons/wireframeSlider.svg';
import { Rect } from '@lib/canvasx/components/Rect';
import {
  FlexRow,
  type RenderedDimensions,
} from '@lib/canvasx/components/FlexRow';
import ProgressBarControl from '../controls/ProgressBarControl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import { TextWithIcons } from '../components/wireframes/TextWithIcons';
import {
  FlexColumn,
  type FlexColumnProps,
} from '@lib/canvasx/components/FlexColumn';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import { SPACER_3 } from '../utils/style';
import { Label } from '../components/wireframes/Label';

const HANDLE_SIZE = 20;
const HANDLE_STROKE = 3;
const HANDLE_PADDING = HANDLE_STROKE + 2;
const SLIDER_OFFSET = HANDLE_SIZE / 2;
const SLIDER_WIDTH = 3;
const PADDING_VERTICAL = 10;
const SLIDER_TEXT_KEY = 'text:value';
const SLIDER_VALUE_MIN_WIDTH = 38;
const SPACER_SMALL = 4;
const LABEL_SPACER = SPACER_3;

class WireframeSlider extends WireframeBase {
  _scaledProgressValue: number;
  labelRowDimensions: RenderedDimensions;
  labelDimensions: RenderedDimensions;
  valueDimensions: RenderedDimensions;
  constructor(fabricObject) {
    super(fabricObject);
    this.labelRowDimensions = { width: 0, height: 0, rows: 0 };
    this.labelDimensions = { width: 0, height: 0, rows: 0 };
    this.valueDimensions = { width: 0, height: 0, rows: 0 };
  }

  tools() {
    return [
      [tools.ToolWireframeLabelPosition],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      this.showLabel ? [tools.ToolWireframeTextColor] : [],
    ];
  }

  static defaultWidth(): number {
    return 200;
  }

  static defaultHeight(): number {
    return 54;
  }

  static iconSvg() {
    return wireframeSlider;
  }

  static externalName(): string {
    return 'Slider';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      progress: new ProgressBarControl({}),
    };
  }

  static labelPositions() {
    return ['textOnly', 'textAndValue', 'noLabel'];
  }

  labelPositions(): string[] {
    return WireframeSlider.labelPositions();
  }

  // Constrain progress value to account for the size of the handle, such that
  // the handle never overflows the slider bar
  constrainProgress() {
    const adjustedPercentage = Math.min(
      Math.max(this.fabricObject.progress, this.minProgress),
      this.maxProgress
    );

    this.fabricObject.set('progress', adjustedPercentage);
  }

  // Since we constrain the progress value, we need to calculate a scaled value
  // that falls in the range of 0 to 1
  updateScaledProgress() {
    const { minProgress, maxProgress } = this;
    const { progress = 0.5 } = this.fabricObject;
    this._scaledProgressValue =
      (progress - minProgress) / (maxProgress - minProgress);
  }

  // Adjust progress value when width changes, so that the value displayed stays
  // the same
  adjustProgress() {
    const { minProgress, maxProgress } = this;
    if (!this.scaledProgressValue) this.updateScaledProgress();

    const adjustedPercentage =
      minProgress + this.scaledProgressValue * (maxProgress - minProgress);

    this.fabricObject.set('progress', adjustedPercentage);
  }

  setSliderValue(value: string) {
    if (value === this.progressDisplayValue.toString()) {
      return;
    }
    this.fabricObject.set('sliderValue', value);
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
    this.fabricObject.controller.undoManager.completeUndoGroup();
  }

  // Percentage progress the handle can slide, adjusting for the size of the handle
  get maxProgress() {
    const { width } = this.fabricObject;
    return (width - (HANDLE_SIZE / 2 + HANDLE_STROKE / 2)) / width;
  }

  get minProgress() {
    return (HANDLE_SIZE + HANDLE_STROKE) / 2 / this.fabricObject.width;
  }

  get scaledProgressValue() {
    if (!this._scaledProgressValue) this.updateScaledProgress();
    return this._scaledProgressValue;
  }

  get progressDisplayValue(): string {
    return `${(this.scaledProgressValue * 100).toFixed(0)}%`;
  }

  get showValue() {
    return this.label?.position === 'textAndValue';
  }

  minDimension() {
    return Math.max(
      this.labelRowDimensions.unwrappedWidth ?? 0,
      WireframeSlider.defaultWidth()
    );
  }

  get childrenWidth() {
    if (!this.showLabel) {
      return 0;
    }
    const valueWidth = this.showValue
      ? Math.max(this.valueDimensions.width, SLIDER_VALUE_MIN_WIDTH)
      : 0;
    return (
      (this.labelDimensions.unwrappedWidth ?? 0) + valueWidth + LABEL_SPACER
    );
  }

  wasSet(keyOrPath: KeyPath, value: any): void {
    if (keyOrPath === 'width') {
      this.adjustProgress();
    }
    if (keyOrPath === 'progress') {
      this.constrainProgress();
    }
    if (
      typeof keyOrPath === 'string' &&
      ['width', 'progress'].includes(keyOrPath)
    ) {
      this.updateScaledProgress();
    }
    super.wasSet(keyOrPath, value);
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      width,
      height,
      scaleX,
      progress = 0.5,
      textColor = 'black',
      fontSize,
      sliderValue,
      textEditing,
    } = this.fabricObject;
    const { showLabel, showValue } = this;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;

    let renderedDimensions;

    const props: Partial<FlexColumnProps> = {};
    // Use explicit dimension if user dragged to resize
    if (this.fabricObject.userHasExplicitlySetDimensions()) {
      props.width = width;
    }

    // It seems some floating point error can cause the slider to partially bleed
    // into the next row of its FlexRow. This prevents that from happening
    const sliderWidth = Math.floor(width);

    // Allow component to shrink when children shrink
    this.resetMaxItemDimensions();

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexColumn
            x={0}
            y={0}
            clip={{ x: 0, y: 0, width, height }}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            horizontalAlignment='stretch'
            {...props}
          >
            <FlexRow
              gap={LABEL_SPACER}
              padding={{ top: SPACER_SMALL, bottom: PADDING_VERTICAL }}
              setRenderedDimensions={dimensions => {
                this.updateMaxItemDimensions({
                  dimensions,
                  useUnwrappedWidth: true,
                });
                this.labelRowDimensions = dimensions;
              }}
              maxRows={1}
            >
              {showLabel && (
                <Label
                  wireframe={this}
                  color={textColor}
                  size={fontSize}
                  padding={{ left: SPACER_SMALL }}
                  setRenderedDimensions={dimensions => {
                    this.updateMaxItemDimensions({
                      dimensions,
                      useUnwrappedWidth: true,
                    });
                    this.labelDimensions = dimensions;
                  }}
                  flexGrow
                />
              )}
              {showValue && (
                <TextWithIcons
                  textEditing={
                    textEditing && this.activeKey.includes(SLIDER_TEXT_KEY)
                  }
                  textKey={SLIDER_TEXT_KEY}
                  color={textColor}
                  size={fontSize}
                  setRenderedDimensions={dimensions => {
                    this.updateMaxItemDimensions({
                      dimensions,
                      useUnwrappedWidth: true,
                    });
                    this.valueDimensions = dimensions;
                  }}
                  onChange={value => {
                    this.setSliderValue(value);
                  }}
                  minWidth={sliderValue ? undefined : SLIDER_VALUE_MIN_WIDTH}
                >
                  {sliderValue || this.progressDisplayValue}
                </TextWithIcons>
              )}
            </FlexRow>
            <FlexRow
              padding={{
                bottom: PADDING_VERTICAL,
                top: this.showLabel ? 0 : PADDING_VERTICAL,
              }}
              maxRows={1}
            >
              <Rect
                y={SLIDER_OFFSET}
                width={
                  sliderWidth * progress - HANDLE_SIZE / 2 - HANDLE_PADDING
                }
                height={SLIDER_WIDTH}
                fill={fill}
              />
              <Rect width={HANDLE_PADDING} />
              <Rect
                borderRadius={HANDLE_SIZE}
                width={HANDLE_SIZE}
                height={HANDLE_SIZE}
                border={{
                  color: fill,
                  opacity: 1,
                  lineWidth: HANDLE_STROKE,
                }}
                fill={'white'}
              />
              <Rect width={HANDLE_PADDING} />
              <Rect
                y={SLIDER_OFFSET}
                width={
                  sliderWidth * (1 - progress) -
                  HANDLE_PADDING -
                  HANDLE_SIZE / 2
                }
                height={SLIDER_WIDTH}
                fill={{ color: fill, opacity: 0.3 }}
              />
            </FlexRow>
          </FlexColumn>
        );

        if (this.updateDimensions) {
          this.updateDimensions = false;
          this._checkSetDimension();

          const newWidth = Math.max(
            WireframeSlider.defaultWidth(),
            this.childrenWidth,
            this.fabricObject.userHasExplicitlySetDimensions()
              ? width
              : this.maxItemDimensions.width
          );

          this.fabricObject.set({
            width: newWidth,
            height: renderedDimensions.height,
          });
          this.fabricObject.canvas.requestRenderAll();
        }
      }
    );
  }
}

export default WireframeSlider;
